import { useState } from "react";
import { toast } from "sonner";
import { api, buildWaLink, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/meta";
import { useSettings, getLeadSource } from "@/context/SettingsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Loader2 } from "lucide-react";

export function LeadDialog({ trip, children }) {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const sources = settings.lead_sources || ["Instagram", "Facebook", "WhatsApp", "Google", "Website", "Referral", "Other"];
  const detectedSource = getLeadSource();
  const [form, setForm] = useState({
    name: "", whatsapp: "", email: "", city: "", travellers: 1,
    departure_date: trip?.departures?.[0]?.date || trip?.next_departure || "",
    source: detectedSource && sources.includes(detectedSource) ? detectedSource : (detectedSource ? "Instagram" : "Website"),
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) {
      toast.error("Please enter your name and WhatsApp number");
      return;
    }
    setLoading(true);
    try {
      const { data: lead } = await api.post("/leads", {
        ...form,
        travellers: Number(form.travellers) || 1,
        trip_id: trip?.id,
        trip_name: trip?.name,
      });
      track("Lead", { content_name: trip?.name || "Enquiry", content_category: "Lead" }, `lead:${lead.id}`);
      const msg = `Hi ${settings.brand_name || "Safarshala"}, I'm interested in ${trip?.name} for ${form.departure_date || "an upcoming departure"}. My name is ${form.name} and I'm looking for a trip for ${form.travellers} traveller(s). Please share the details.`;
      toast.success("Thanks! Redirecting you to WhatsApp…");
      window.open(buildWaLink(settings.whatsapp_number, msg), "_blank");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const departures = trip?.departures || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{children}</div>
      <DialogContent className="max-w-md max-h-[92dvh] overflow-y-auto overscroll-contain p-5 sm:p-6 max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:w-full max-sm:max-w-full max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl sm:text-3xl text-left pr-8 leading-tight flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-[#25D366]" /> Query on WhatsApp
          </DialogTitle>
          <DialogDescription>Quick details and we'll continue the chat on WhatsApp.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>WhatsApp *</Label>
              <Input data-testid="lead-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="91XXXXXXXXXX" />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="lead-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City" />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="lead-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Departure</Label>
              <Select value={form.departure_date} onValueChange={(v) => set("departure_date", v)}>
                <SelectTrigger data-testid="lead-departure"><SelectValue placeholder="Select date" /></SelectTrigger>
                <SelectContent>
                  {departures.length === 0 && <SelectItem value="flexible">Flexible</SelectItem>}
                  {departures.map((d) => <SelectItem key={d.id} value={d.date}>{d.date}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Travellers</Label>
              <Input type="number" min="1" data-testid="lead-travellers" value={form.travellers} onChange={(e) => set("travellers", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>How did you hear about us?</Label>
            <Select value={form.source} onValueChange={(v) => set("source", v)}>
              <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} data-testid="lead-submit" className="w-full rounded-full bg-[#25D366] hover:bg-[#1da851] text-white">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to WhatsApp"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
